import { AxiosInstance } from "axios"
import { Allergen, CreateProduct, Product, ProductGroups, UpdateStockPayload } from "./productsTypes"
import { ServiceResponse } from "../../../../core/api/apiTypes"

export enum MerchantProductsQuery {
    LIST = "products-for-merchant",
    PRODUCT_DETAILS = "details-for-product",
}

// -- Fetch Products --
type FetchProductsParams = Readonly<{
    merchantId: number
    priceListId?: number
    shopId?: string
    productGroupId?: number
    isActive?: boolean
}>

export type FetchProductsResponse = {
    productGroups: ProductGroups[]
}

export async function fetchProducts(
    { merchantId, ...rest }: FetchProductsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchProductsResponse> {
    const url = `/merchants/${merchantId}/products`

    const response = await axios.get(url, {
        params: rest,
        signal,
    })

    return response.data
}

// -- Create Product --
type CreateProductParams = { product: CreateProduct; merchantId: number }
type CreateProductResponse = ServiceResponse<{}>

export async function createProduct(
    params: CreateProductParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateProductResponse> {
    const { product, merchantId } = params
    const response = await axios.post(
        "/products",
        {
            product: {
                ...product,
                merchant: {
                    id: merchantId,
                },
            },
        },
        {
            signal,
        },
    )
    return response.data
}

// -- Create Product --
export type UpdateProductBody = CreateProduct & {
    merchantId: number
}
type UpdateProductParams = {
    productId: number
    product: CreateProduct
}
type UpdateProductResponse = ServiceResponse<{}>

export async function updateProduct(
    params: UpdateProductParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateProductResponse> {
    const { product, productId } = params
    const response = await axios.post(
        `/products/${productId}`,
        {
            product: {
                ...product,
            },
        },
        {
            signal,
        },
    )
    return response.data
}

// -- Delete Product --
type DeleteProductParams = Readonly<{
    productId: number
}>

export type DeleteProductResponse = ServiceResponse<{}>

export async function deleteProduct(
    params: DeleteProductParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteProductResponse> {
    const { productId } = params
    const url = `/products/${productId}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

// -- Get product details --
type GetProductDetailsParams = Readonly<{
    productId: number
}>

export type GetProductDetailsResponse = {
    product: Product
}

export async function getProductDetails(
    params: GetProductDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetProductDetailsResponse> {
    const { productId } = params
    const url = `/products/${productId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}
// -- Move product --

export type MoveProductBody = Readonly<{
    shop: {
        id: string
    }
    moveTo: {
        productGroup?: {
            id: number
        }
        position: number
    }
}>

type MoveProductParams = Readonly<{
    productId: number
    position: MoveProductBody
}>

type MoveProductResponse = ServiceResponse<{}>

export async function moveProduct(
    params: MoveProductParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MoveProductResponse> {
    const { productId, position } = params
    const response = await axios.post(`/products/${productId}/move`, position, {
        signal,
    })

    return response.data
}

// Allergens
export type AllergensResponse = {
    allergens: Allergen[]
}

export async function fetchAllergens(
    params: {},
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AllergensResponse> {
    const url = `/allergens`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// -- Update Stock --
type UpdateStockResponse = ServiceResponse<{}>

export async function updateStock(
    params: UpdateStockPayload,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateStockResponse> {
    const { productId, inventory, sales } = params
    const url = `/products/${productId}/stock`

    const response = await axios.post(
        url,
        {
            stock: {
                ...(inventory ? { inventory } : {}),
                ...(sales ? { sales } : {}),
            },
        },
        { signal },
    )
    return response.data
}
